import { isSafari } from "@/libs/software"

export const isVideoFrameCallbackSupported =
  typeof HTMLVideoElement.prototype.requestVideoFrameCallback === "function"

export const requestVideoFrameCallback = (video: HTMLVideoElement, cb: FrameRequestCallback) => {
  if (isVideoFrameCallbackSupported) return video.requestVideoFrameCallback(cb)
  return requestAnimationFrame(cb)
}
export const cancelVideoFrameCallback = (video: HTMLVideoElement, id: number) => {
  if (isVideoFrameCallbackSupported && video.isConnected) video.cancelVideoFrameCallback(id)
  else cancelAnimationFrame(id)
}

export const createPreloadedVideo = (src: string): Promise<HTMLVideoElement> =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video")
    video.src = src
    video.preload = "auto"
    video.muted = true
    video.loop = false
    video.autoplay = false
    video.playsInline = true
    video.controls = false

    /* 1x1 transparent pixel https://png-pixel.com */
    const pixel =
      "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
    /*
     * Removes the ugly "Play" button displayed before the video starts playing on some Android devices
     * The original fix targeted Android Instagram in-app browser
     * https://stackoverflow.com/a/73309379
     */
    video.poster = pixel

    video.addEventListener("loadeddata", () => resolve(video), { once: true })
    video.addEventListener("error", reject)

    video.load()
  })

export const attachVideoSource = (video: HTMLVideoElement, preloaded: HTMLVideoElement) => {
  /* 1x1 transparent pixel https://png-pixel.com */
  const pixel = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
  /*
   * Removes the ugly "Play" button displayed before the video starts playing on some Android devices
   * The original fix targeted Android Instagram in-app browser
   * https://stackoverflow.com/a/73309379
   */
  video.poster = pixel

  video.src = preloaded.src
  video.muted = true
  video.loop = false
  video.autoplay = false
  video.playsInline = true
  video.currentTime = 0

  video.style.opacity = "0"
  video.style.transition = "opacity 0.15s ease-out"
  video.addEventListener(
    "play",
    () => {
      video.style.opacity = "1"
      video.autoplay = false
    },
    { once: true },
  )
}

/**
 * Manually creates a video element with the given MediaProvider as its source.
 *
 * At the moment, this is the only known reliable way to create a `<video muted playsinline autoplay />`, set its srcObject, and append it to DOM on the same execution tick, addressing the following issues:
 *
 * 1. having `<video muted playsinline />` and setting its `srcObject` with subsequent call to `.play()` leads
 *    to an error on some Android devices (LIQA-1259)
 * 2. having `<video muted playsinline autoplay />` and setting its `.srcObject` does not start the playback since
 *    the element creation and the `.srcObject` assignment occur on the different execution ticks
 */
export const createVideo = (src: MediaProvider) => {
  const video = document.createElement("video")

  video.muted = true
  video.autoplay = true
  video.playsInline = true
  video.controls = false
  video.srcObject = src

  /* 1x1 transparent pixel https://png-pixel.com */
  const pixel = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
  /*
   * Removes the ugly "Play" button displayed before the video starts playing on some Android devices
   * The original fix targeted Android Instagram in-app browser
   * https://stackoverflow.com/a/73309379
   */
  video.poster = pixel

  /*
   * A workaround to hide the native "play" button rendered on Safari (LIQ-155).
   * The issue is only observable when device is running on battery and not connected to a charger.
   * The CSS approach (https://gist.github.com/benfrain/8952179) does not work as of Safari 17.x :(
   */
  if (isSafari) {
    video.style.opacity = "0"
    video.addEventListener(
      "play",
      () => {
        video.style.opacity = ""
        // The "play" button is only displayed if the "autoplay" attribute is set
        video.autoplay = false
      },
      { once: true },
    )
  }

  return video
}
